import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Input } from "@/components/ui/input";
import { usd, shortDate, daysFromNow } from "@/lib/format";
import { Search, Ship, MapPin, CheckCircle2, Circle, AlertTriangle } from "lucide-react";

export default function Shipments() {
  const [params] = useSearchParams();
  const focusId = params.get("focus");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/shipments").then(r => {
      setItems(r.data);
      const initial = focusId ? r.data.find(s => s.id === focusId) : r.data[0];
      setSelected(initial || r.data[0]);
    });
  }, [focusId]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    if (!s) return items;
    return items.filter(it =>
      it.id.toLowerCase().includes(s) ||
      it.bl_number.toLowerCase().includes(s) ||
      (it.container_numbers || []).some(c => c.toLowerCase().includes(s)) ||
      (it.po_numbers || []).some(p => p.toLowerCase().includes(s)) ||
      it.origin_name.toLowerCase().includes(s) ||
      it.destination_name.toLowerCase().includes(s)
    );
  }, [items, q]);

  return (
    <div className="space-y-6 animate-slide-up" data-testid="shipments-page">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Shipment Visibility</h1>
        <p className="text-sm text-zinc-500 mt-1">Real-time tracking with predictive ETA, milestones and exception classification.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* List */}
        <div className="lg:col-span-5 bg-white border border-zinc-200">
          <div className="px-4 py-3 border-b border-zinc-200 flex items-center gap-2">
            <Search className="w-4 h-4 text-zinc-400" />
            <Input
              data-testid="shipment-search"
              placeholder="Search SHP / BL / Container / PO / Port..."
              value={q} onChange={e => setQ(e.target.value)}
              className="border-0 focus-visible:ring-0 h-9 px-0"
            />
            <span className="text-xs text-zinc-500 metric">{filtered.length}</span>
          </div>
          <div className="max-h-[640px] overflow-auto" data-testid="shipments-list">
            {filtered.map(s => {
              const lfdDays = daysFromNow(s.last_free_day);
              const active = selected?.id === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`shipment-row-${s.id}`}
                  onClick={() => setSelected(s)}
                  className={`w-full text-left px-4 py-3 border-b border-zinc-100 transition-colors ${active ? 'bg-[#E6EBF6] border-l-2 border-l-[#002FA7]' : 'hover:bg-zinc-50/60'}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="metric text-sm font-medium">{s.id}</div>
                    <StatusBadge value={s.status} />
                  </div>
                  <div className="text-sm text-zinc-700">{s.origin_name} <span className="text-zinc-400">→</span> {s.destination_name}</div>
                  <div className="flex items-center justify-between mt-1.5 text-[11px] text-zinc-500 metric">
                    <span>{s.carrier} · {s.vessel}</span>
                    <span>{s.delay_days > 0 ? <span className="text-[#B45309]">+{s.delay_days}d</span> : <span>ETA {shortDate(s.eta_actual)}</span>}</span>
                  </div>
                  {lfdDays !== null && lfdDays <= 3 && s.status !== "DELIVERED" && (
                    <div className="mt-1 text-[10px] uppercase tracking-[0.12em] text-[#B45309] flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> LFD in {lfdDays}d
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-7 space-y-4">
          {selected && (
            <>
              <div className="bg-white border border-zinc-200 p-6" data-testid="shipment-detail">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="data-label mb-1">Shipment</div>
                    <h2 className="font-heading text-2xl font-bold metric">{selected.id}</h2>
                    <div className="mt-1 text-sm text-zinc-600 flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-[#002FA7]" />
                      {selected.origin_name} <span className="text-zinc-400">→</span> {selected.destination_name}
                    </div>
                  </div>
                  <div className="text-right space-y-1">
                    <StatusBadge value={selected.status} />
                    <div className="data-label">{selected.exception || ""}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
                  {[
                    ["Carrier / Vessel", `${selected.carrier} · ${selected.vessel} ${selected.voyage}`],
                    ["BL", selected.bl_number],
                    ["Containers", `${selected.container_count} × ${selected.container_type}`],
                    ["ETD", shortDate(selected.etd)],
                    ["ETA (Planned)", shortDate(selected.eta_planned)],
                    ["ETA (Predicted)", `${shortDate(selected.eta_actual)}${selected.delay_days > 0 ? ` +${selected.delay_days}d` : ''}`],
                    ["Free Time", `${selected.free_time_days}d`],
                    ["LFD", shortDate(selected.last_free_day)],
                    ["Freight Cost", usd(selected.freight_cost_usd)],
                  ].map(([k, v]) => (
                    <div key={k} className="bg-white p-3">
                      <div className="data-label">{k}</div>
                      <div className="text-sm mt-1 metric">{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Map / route */}
              <div className="bg-white border border-zinc-200 p-6">
                <div className="data-label mb-4 flex items-center gap-2"><Ship className="w-3.5 h-3.5" /> Route Progress</div>
                <div className="relative h-24 bg-zinc-50 border border-zinc-100">
                  <div className="absolute inset-y-0 left-0 right-0 flex items-center px-8">
                    <div className="flex-1 h-px bg-zinc-300 relative">
                      <div
                        className="absolute top-0 left-0 h-px bg-[#002FA7]"
                        style={{ width: `${selected.status === 'DELIVERED' ? 100 : selected.status === 'ON_VESSEL' ? 55 : selected.status === 'AT_DESTINATION' ? 85 : selected.status === 'AT_ORIGIN' ? 15 : selected.status === 'BOOKED' ? 5 : 65}%` }}
                      />
                      <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-zinc-900" style={{ left: 0 }} />
                      <div className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-[#002FA7]" style={{ right: 0 }} />
                      <div
                        className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-[#002FA7]"
                        style={{ left: `${selected.status === 'DELIVERED' ? 100 : selected.status === 'ON_VESSEL' ? 55 : selected.status === 'AT_DESTINATION' ? 85 : selected.status === 'AT_ORIGIN' ? 15 : 5}%`, transform: 'translate(-50%, -50%)' }}
                      />
                    </div>
                  </div>
                  <div className="absolute left-3 bottom-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500">{selected.origin_name}</div>
                  <div className="absolute right-3 bottom-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500">{selected.destination_name}</div>
                </div>
              </div>

              {/* Milestones */}
              <div className="bg-white border border-zinc-200 p-6">
                <div className="data-label mb-4">Milestones</div>
                <div className="space-y-1">
                  {(selected.milestones || []).map((m, i) => (
                    <div key={m.code} className="flex items-center gap-3 py-2 border-b border-zinc-100 last:border-b-0">
                      {m.completed ? <CheckCircle2 className="w-4 h-4 text-[#16A34A] flex-shrink-0" /> : <Circle className="w-4 h-4 text-zinc-300 flex-shrink-0" />}
                      <div className="flex-1 text-sm">
                        <span className={m.completed ? "text-zinc-900" : "text-zinc-500"}>{m.label}</span>
                      </div>
                      <div className="text-xs metric text-zinc-500">{shortDate(m.date)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Containers */}
              <div className="bg-white border border-zinc-200 p-6">
                <div className="data-label mb-3">Container Numbers</div>
                <div className="flex flex-wrap gap-2">
                  {(selected.container_numbers || []).map(c => (
                    <span key={c} className="metric text-xs px-2.5 py-1 bg-zinc-100 border border-zinc-200">{c}</span>
                  ))}
                </div>
                <div className="data-label mt-5 mb-2">Linked POs</div>
                <div className="flex flex-wrap gap-2">
                  {(selected.po_numbers || []).map(p => (
                    <span key={p} className="metric text-xs px-2.5 py-1 bg-[#E6EBF6] text-[#002FA7] border border-[#A7B4D9]">{p}</span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
